import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Seasonal offer with a fixed deadline or a shared daily Berlin deadline.
 * @extends {Component<{deadline?: HTMLElement, days?: HTMLElement, hours?: HTMLElement, minutes?: HTMLElement, seconds?: HTMLElement}>}
 */
class KnOfferCountdown extends Component {
  #expiry;
  #visibility;
  #selection = 0;
  #end = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#visibility?.abort();
    this.#visibility = new AbortController();
    document.addEventListener('visibilitychange', () => this.#start(), { signal: this.#visibility.signal });
    this.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, {
      signal: this.#visibility.signal,
    });
    this.#start();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#start();
  }

  disconnectedCallback() {
    this.#selection++;
    clearTimeout(this.#expiry);
    this.#visibility?.abort();
    super.disconnectedCallback();
  }

  #start() {
    clearTimeout(this.#expiry);
    if (document.hidden) return;
    const daily = this.dataset.daily === 'true';
    const now = Date.now();
    const end = daily ? nextBerlinMidnight(now) : Date.parse(this.dataset.end ?? '');
    const remaining = end - now;
    const deadline = this.refs.deadline;
    if (!deadline) return;
    if (this.dataset.discounted !== 'true' || !Number.isFinite(end) || remaining <= 0) {
      this.hidden = true;
      return;
    }
    this.hidden = false;
    this.#end = end;
    const locale = document.documentElement.lang || 'en';
    deadline.dataset.end = new Date(end).toISOString();
    deadline.title = new Intl.DateTimeFormat(locale, {
      dateStyle: 'medium', timeStyle: 'short',
      timeZone: 'Europe/Berlin',
    }).format(end) + ' (Europe/Berlin)';
    this.#tick();
  }

  #tick() {
    clearTimeout(this.#expiry);
    const now = Date.now();
    if (now >= this.#end) {
      this.#start();
      return;
    }
    const seconds = Math.ceil((this.#end - now) / 1000);
    const values = {
      days: Math.floor(seconds / 86400),
      hours: Math.floor((seconds % 86400) / 3600),
      minutes: Math.floor((seconds % 3600) / 60),
      seconds: seconds % 60,
    };
    for (const [name, value] of Object.entries(values)) {
      const element = this.refs[name];
      const text = name === 'days' ? String(value) : String(value).padStart(2, '0');
      if (element instanceof HTMLElement && element.textContent !== text) element.textContent = text;
    }
    this.refs.deadline?.removeAttribute('hidden');
    this.#expiry = setTimeout(() => this.#tick(), 1000 - (now % 1000));
  }

  /** @param {import('@shopify/events').ProductSelectEvent} event */
  #onProductSelect = async (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    const selection = ++this.#selection;
    const controller = this.#visibility;
    try {
      const { detail } = await event.promise;
      if (!detail?.html || selection !== this.#selection || controller?.signal.aborted) return;
      if (detail.newProduct) this.dataset.productId = String(detail.newProduct.id);
      else if (detail.productId && detail.productId !== this.dataset.productId) return;

      const nextOffer = detail.html.querySelector(`kn-offer-countdown[data-offer-id="${CSS.escape(this.dataset.offerId ?? '')}"]`);
      this.dataset.discounted = nextOffer?.getAttribute('data-discounted') ?? 'false';
      this.#start();
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[kn-offer-countdown] Variant update failed:', error);
    }
  };
}

/** Resolve the next calendar midnight in Berlin, including 23/25-hour days. */
function nextBerlinMidnight(now) {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Berlin', year: 'numeric', month: 'numeric', day: 'numeric',
    hour: 'numeric', minute: 'numeric', second: 'numeric', hourCycle: 'h23',
  });
  const partsAt = (time) => Object.fromEntries(formatter.formatToParts(time).map(({ type, value }) => [type, Number(value)]));
  const today = partsAt(now);
  const target = Date.UTC(today.year, today.month - 1, today.day + 1);
  let midnight = target;
  // Convert the target wall time to UTC using the offset at the target itself.
  for (let attempt = 0; attempt < 3; attempt++) {
    const local = partsAt(midnight);
    const correction = target - Date.UTC(local.year, local.month - 1, local.day, local.hour, local.minute, local.second);
    midnight += correction;
    if (correction === 0) break;
  }
  return midnight;
}

if (!customElements.get('kn-offer-countdown')) customElements.define('kn-offer-countdown', KnOfferCountdown);
